<template>
  <div>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="3" rounded="xl">
            <!-- Page Header -->
            <v-card-title class="pb-3 pt-5 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <h2 class="text-h5 font-weight-bold primary--text mb-1">
                    {{ isDeletePage() ? "Deleted Investments" : "Investment Management" }}
                  </h2>
                  <p class="text-body-2 grey--text ma-0">
                    {{ isDeletePage() ? "Restore deleted investment records" : "Track and manage all investment transactions" }}
                  </p>
                </v-col>
                <v-col cols="12" md="4" class="pa-0" v-if="!isDeletePage()">
                  <div class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="2"
                      rounded
                      to="/investments/create"
                      class="px-5"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Add New Investment
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider class="mx-6"></v-divider>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-2 pt-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-3" color="primary">mdi-filter-variant</v-icon>
                    <span class="text-subtitle-1 font-weight-medium">Filter Investments</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      small
                      rounded="pill"
                      class="mr-3"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active Filter(s)
                    </v-chip>
                    <v-btn
                      text
                      small
                      color="error"
                      rounded
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left small>mdi-filter-remove</v-icon>
                      Clear All
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-6 pb-6">
              <v-row class="align-center">
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Investments"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    rounded
                    dense
                    clearable
                    placeholder="Product, user, amount..."
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Active Status Filter -->
                <v-col cols="12" sm="6" md="2">
                  <v-select
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Active Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>

                <!-- Investment Status Filter -->
                <v-col cols="12" sm="6" md="3">
                  <v-select
                    v-model="filter.status"
                    :items="investmentStatuses"
                    label="Investment Status"
                    prepend-inner-icon="mdi-clipboard-check"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>

                <!-- Payment Mode Filter -->
                <v-col cols="12" sm="6" md="3">
                  <v-select
                    v-model="filter.payment_mode"
                    :items="paymentModes"
                    label="Payment Mode"
                    prepend-inner-icon="mdi-credit-card"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Investments Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" rounded="lg">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="24">mdi-trending-up</v-icon>
                    <span class="text-h6 font-weight-medium">Investments List</span>
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined class="mr-3">
                      <v-icon left small>mdi-chart-line</v-icon>
                      {{ count }} {{ count === 1 ? "Investment" : "Investments" }} Found
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      Showing {{ (page - 1) * itemsPerPage + 1 }} -
                      {{ Math.min(page * itemsPerPage, count) }} of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-3">
                      <span class="text-caption grey--text mr-2">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn small outlined :value="10">10</v-btn>
                        <v-btn small outlined :value="25">25</v-btn>
                        <v-btn small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      small
                      color="success"
                      outlined
                      rounded
                      @click="exportToExcel"
                      class="px-3"
                    >
                      <v-icon left small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <table-skeleton v-if="isLoading" :rows="10" :cols="7" />

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="investments"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Active Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ item.is_active ? "mdi-check-circle" : "mdi-close-circle" }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Investment Status Column -->
                <template #[`item.status`]="{ item }">
                  <v-chip
                    :color="getInvestmentStatusColor(item.status)"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ getInvestmentStatusIcon(item.status) }}
                    </v-icon>
                    {{ getInvestmentStatusText(item.status) }}
                  </v-chip>
                </template>

                <!-- Product & User Info Column -->
                <template #[`item.investment_info`]="{ item }">
                  <div>
                    <div class="font-weight-medium mb-1">
                      {{ item.product?.product_name || "N/A" }}
                    </div>
                    <div class="text-caption grey--text d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-account</v-icon>
                      {{ getFullUserName(item.user) }}
                    </div>
                  </div>
                </template>

                <!-- Investment Amount Column -->
                <template #[`item.invested_amount`]="{ item }">
                  <div class="text-center">
                    <div class="font-weight-bold text-h6 success--text">
                      {{ formatCurrency(item.invested_amount) }}
                    </div>
                  </div>
                </template>

                <!-- Payment Mode Column -->
                <template #[`item.payment_mode`]="{ item }">
                  <v-chip
                    :color="getPaymentModeColor(item.payment_mode)"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ getPaymentModeIcon(item.payment_mode) }}
                    </v-icon>
                    {{ getPaymentModeText(item.payment_mode) }}
                  </v-chip>
                </template>

                <!-- Documents Column -->
                <template #[`item.documents`]="{ item }">
                  <div class="d-flex">
                    <v-tooltip bottom v-if="item.upi_screenshot">
                      <template #activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          x-small
                          color="blue"
                          rounded
                          class="mr-1"
                          @click="viewImage(item.upi_screenshot, 'UPI Screenshot')"
                        >
                          <v-icon x-small>mdi-credit-card</v-icon>
                        </v-btn>
                      </template>
                      <span>View UPI Screenshot</span>
                    </v-tooltip>
                    <v-tooltip bottom v-if="item.bank_statement">
                      <template #activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          x-small
                          color="green"
                          rounded
                          @click="viewImage(item.bank_statement, 'Bank Statement')"
                        >
                          <v-icon x-small>mdi-bank</v-icon>
                        </v-btn>
                      </template>
                      <span>View Bank Statement</span>
                    </v-tooltip>
                    <span v-if="!item.upi_screenshot && !item.bank_statement" class="text-caption grey--text">
                      No documents
                    </span>
                  </div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- Approval Actions for Pending Investments -->
                      <template v-if="item.status == 0">
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <v-btn
                              small
                              color="success"
                              rounded
                              class="mr-1"
                              @click="openApproveDialog(item.id)"
                              :loading="approvingId === item.id"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small>mdi-check</v-icon>
                            </v-btn>
                          </template>
                          <span>Approve Investment</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <v-btn
                              small
                              color="error"
                              rounded
                              class="mr-1"
                              @click="openRejectDialog(item.id)"
                              :loading="rejectingId === item.id"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small>mdi-close</v-icon>
                            </v-btn>
                          </template>
                          <span>Reject Investment</span>
                        </v-tooltip>
                      </template>

                      <!-- View Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            small
                            color="primary"
                            rounded
                            :to="`/investments/${encryptIt(item.id)}`"
                            :class="item.status == 0 ? 'mr-1' : 'mr-2'"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-eye</v-icon>
                          </v-btn>
                        </template>
                        <span>View Investment</span>
                      </v-tooltip>

                      <!-- Delete Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            small
                            color="error"
                            rounded
                            @click="openDeleteDialog(item.id)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Investment</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <!-- Restore Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            small
                            color="success"
                            rounded
                            @click="restoreData(item.id)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Investment</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3">mdi-trending-down</v-icon>
                    <h3 class="text-h6 grey--text mt-4 mb-2">No Investments Found</h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No investments match your current filters. Try adjusting your search criteria."
                          : isDeletePage()
                          ? "No deleted investments to restore."
                          : "No investments have been created yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/investments/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Investment
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-body-2 grey--text font-weight-medium">
                      Showing {{ (page - 1) * itemsPerPage + 1 }} to
                      {{ Math.min(page * itemsPerPage, count) }} of
                      {{ count }} investments
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      small
                      outlined
                      rounded
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-3"
                    >
                      <v-icon small left>mdi-chevron-left</v-icon>
                      Previous
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      circle
                      class="mx-2"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      small
                      outlined
                      rounded
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-3"
                    >
                      Next
                      <v-icon small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-body-2 grey--text mr-3 font-weight-medium">Go to page:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details="auto"
                      rounded
                      hide-spin-buttons
                      class="no-spin-arrows"
                      style="max-width: 90px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Confirmation Dialog -->
      <DeleteConfirmationDialog
        v-if="selectedId !== null"
        :itemId="selectedId"
        @delete="deleteData"
        ref="deleteDialog"
      />

      <!-- Approve Confirmation Dialog -->
      <v-dialog v-model="approveDialog" max-width="400" persistent>
        <v-card rounded="lg">
          <v-card-title class="text-h6 success--text pa-6 pb-2">
            <v-icon left color="success">mdi-check-circle</v-icon>
            Approve Investment
          </v-card-title>
          <v-card-text class="px-6 pb-2">
            Are you sure you want to approve this investment?
          </v-card-text>
          <v-card-actions class="px-6 pb-6">
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="approveDialog = false"
              :disabled="approvingId !== null"
            >
              Cancel
            </v-btn>
            <v-btn
              color="success"
              rounded
              @click="approveInvestment"
              :loading="approvingId !== null"
            >
              Approve
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Reject Confirmation Dialog -->
      <v-dialog v-model="rejectDialog" max-width="400" persistent>
        <v-card rounded="lg">
          <v-card-title class="text-h6 error--text pa-6 pb-2">
            <v-icon left color="error">mdi-close-circle</v-icon>
            Reject Investment
          </v-card-title>
          <v-card-text class="px-6 pb-2">
            Are you sure you want to reject this investment?
          </v-card-text>
          <v-card-actions class="px-6 pb-6">
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="rejectDialog = false"
              :disabled="rejectingId !== null"
            >
              Cancel
            </v-btn>
            <v-btn
              color="error"
              rounded
              @click="rejectInvestment"
              :loading="rejectingId !== null"
            >
              Reject
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Image Viewer Dialog -->
      <v-dialog v-model="imageDialog" max-width="800">
        <v-card rounded="lg">
          <v-card-title class="text-h6 pa-6 pb-2">
            <v-icon left color="primary">mdi-image</v-icon>
            {{ imageTitle }}
          </v-card-title>
          <v-card-text class="text-center pa-6">
            <v-img
              v-if="currentImageUrl"
              :src="currentImageUrl"
              max-width="100%"
              max-height="500px"
              contain
              class="mx-auto"
            ></v-img>
          </v-card-text>
          <v-card-actions class="px-6 pb-6">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              rounded
              @click="imageDialog = false"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar for notifications -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
      >
        {{ snackbar.message }}
        <template #action="{ attrs }">
          <v-btn
            color="white"
            text
            v-bind="attrs"
            @click="snackbar.show = false"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";

export default {
  components: { DeleteConfirmationDialog },
  name: "investmentsPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        status: "",
        payment_mode: "",
        show_deleted: false,
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      jumpToPage: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      investments: [],
      statuses: [
        {
          text: "All Status",
          value: "",
        },
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      investmentStatuses: [
        {
          text: "All Statuses",
          value: "",
        },
        { text: "Pending", value: "0" },
        { text: "Approved", value: "1" },
        { text: "Rejected", value: "2" },
      ],
      paymentModes: [
        {
          text: "All Payment Modes",
          value: "",
        },
        { text: "Wallet", value: "1" },
        { text: "Online", value: "2" },
        { text: "Cash", value: "3" },
      ],
      isLoading: false,
      selectedId: null,
      approveDialog: false,
      rejectDialog: false,
      imageDialog: false,
      currentImageUrl: null,
      imageTitle: "",
      selectedInvestmentId: null,
      approvingId: null,
      rejectingId: null,
      snackbar: {
        show: false,
        message: "",
        color: "success",
        timeout: 4000,
      },
      headers: [
        { text: "Sr No.", value: "sr_no", sortable: false, width: "70px" },
        { text: "Active", value: "is_active", width: "120px" },
        { text: "Status", value: "status", width: "120px" },
        {
          text: "Investment Info",
          value: "investment_info",
          sortable: false,
          width: "200px",
        },
        {
          text: "Amount",
          value: "invested_amount",
          width: "150px",
        },
        {
          text: "Payment Mode",
          value: "payment_mode",
          width: "120px",
        },
        {
          text: "Documents",
          value: "documents",
          sortable: false,
          width: "120px",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "180px",
          align: "center",
        },
      ],
    };
  },
  computed: {
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.is_active ||
        this.filter.status ||
        this.filter.payment_mode
      );
    },
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      console.log("Getting investment data...");
      this.isLoading = true;

      // Set filters with default values
      const filters = {
        page: this.page,
        rowsPerPage: this.itemsPerPage,
        show_deleted: this.$route.query.open_trash ? 1 : 0,
        search_keyword: this.filter.search_keyword || "",
        is_active: this.filter.is_active || "",
        status: this.filter.status || "",
        payment_mode: this.filter.payment_mode || "",
      };

      let query = new URLSearchParams(filters);

      try {
        const response = await axios.get(`investments?${query}`);
        this.investments = response.data.data || [];
        this.count = response.data.count || 0;
        this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (error) {
        console.error("Failed to fetch investments:", error);
        this.investments = [];
        this.count = 0;
      }

      this.isLoading = false;
    },

    clearAllFilters() {
      this.filter = {
        search_keyword: "",
        is_active: "",
        status: "",
        payment_mode: "",
        show_deleted: false,
      };
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active) count++;
      if (this.filter.status) count++;
      if (this.filter.payment_mode) count++;
      return count;
    },

    isDeletePage() {
      return !!this.$route.query.open_trash;
    },

    getFullUserName(user) {
      if (!user) return "Unknown User";
      return `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Unknown User";
    },

    getInvestmentStatusColor(status) {
      switch (parseInt(status)) {
        case 0: return "orange";  // Pending
        case 1: return "success"; // Approved
        case 2: return "error";   // Rejected
        default: return "grey";
      }
    },

    getInvestmentStatusIcon(status) {
      switch (parseInt(status)) {
        case 0: return "mdi-clock-outline";  // Pending
        case 1: return "mdi-check-circle";   // Approved
        case 2: return "mdi-close-circle";   // Rejected
        default: return "mdi-help-circle";
      }
    },

    getInvestmentStatusText(status) {
      switch (parseInt(status)) {
        case 0: return "Pending";
        case 1: return "Approved";
        case 2: return "Rejected";
        default: return "Unknown";
      }
    },

    getPaymentModeColor(mode) {
      switch (parseInt(mode)) {
        case 1: return "blue";   // Wallet
        case 2: return "green";  // Online
        case 3: return "orange"; // Cash
        default: return "grey";
      }
    },

    getPaymentModeIcon(mode) {
      switch (parseInt(mode)) {
        case 1: return "mdi-wallet";      // Wallet
        case 2: return "mdi-credit-card"; // Online
        case 3: return "mdi-cash";        // Cash
        default: return "mdi-help-circle";
      }
    },

    getPaymentModeText(mode) {
      switch (parseInt(mode)) {
        case 1: return "Wallet";
        case 2: return "Online";
        case 3: return "Cash";
        default: return "Unknown";
      }
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
      }).format(parseFloat(amount) || 0);
    },

    changeItemsPerPageToggle(value) {
      if (value !== null && value !== undefined) {
        this.itemsPerPage = value;
        this.selectedItemsPerPage = value;
        this.page = 1;
        this.getData();
      }
    },

    goToPage() {
      const pageNum = parseInt(this.jumpToPage);
      if (pageNum >= 1 && pageNum <= this.paginationLength) {
        this.page = pageNum;
      } else {
        this.jumpToPage = this.page;
      }
    },

    exportToExcel() {
      console.log("Export to Excel clicked");
      // Implementation for Excel export
    },

    openDeleteDialog(id) {
      this.selectedId = id;
      this.$nextTick(() => {
        this.$refs.deleteDialog.openDialog();
      });
    },

    async deleteData(id) {
      try {
        await axios.post(`/investments/delete/${this.encryptIt(id)}`);
        this.showSnackbar("Investment deleted successfully!", "success");
        this.getData();
      } catch (error) {
        console.error("Failed to delete investment:", error);
        this.showSnackbar("Failed to delete investment. Please try again.", "error");
      }
    },

    async restoreData(id) {
      try {
        await axios.post(`/investments/restore/${this.encryptIt(id)}`);
        this.showSnackbar("Investment restored successfully!", "success");
        this.getData();
      } catch (error) {
        console.error("Failed to restore investment:", error);
        this.showSnackbar("Failed to restore investment. Please try again.", "error");
      }
    },

    openApproveDialog(id) {
      this.selectedInvestmentId = id;
      this.approveDialog = true;
    },

    openRejectDialog(id) {
      this.selectedInvestmentId = id;
      this.rejectDialog = true;
    },

    async approveInvestment() {
      this.approvingId = this.selectedInvestmentId;
      try {
        await axios.post(`/investments/approve/${this.encryptIt(this.selectedInvestmentId)}`);
        this.approveDialog = false;
        this.showSnackbar("Investment approved successfully!", "success");
        this.getData();
      } catch (error) {
        console.error("Failed to approve investment:", error);
        const message = error.response?.data?.message || "Failed to approve investment. Please try again.";
        this.showSnackbar(message, "error");
      } finally {
        this.approvingId = null;
      }
    },

    async rejectInvestment() {
      this.rejectingId = this.selectedInvestmentId;
      try {
        await axios.post(`/investments/reject/${this.encryptIt(this.selectedInvestmentId)}`);
        this.rejectDialog = false;
        this.showSnackbar("Investment rejected successfully!", "success");
        this.getData();
      } catch (error) {
        console.error("Failed to reject investment:", error);
        const message = error.response?.data?.message || "Failed to reject investment. Please try again.";
        this.showSnackbar(message, "error");
      } finally {
        this.rejectingId = null;
      }
    },

    viewImage(imagePath, title) {
      this.currentImageUrl = `${this.mediaUrl}${imagePath}`;
      this.imageTitle = title;
      this.imageDialog = true;
    },

    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
  },
};
</script>

<style scoped>
/* Enhanced Table Styles */
.enhanced-table >>> tbody tr {
  transition: all 0.3s ease;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.enhanced-table >>> tbody tr:nth-child(odd) {
  background-color: #fafbfc;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px 12px 0 0;
}

.enhanced-table >>> .v-data-table-header th {
  font-weight: 600 !important;
  color: #2c3e50 !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 16px 12px !important;
  font-size: 0.875rem !important;
}

.enhanced-table >>> tbody td {
  padding: 16px 12px !important;
  border-bottom: 1px solid #f1f3f4 !important;
}

/* Card Enhancements */
.v-card {
  border-radius: 12px !important;
  border: 1px solid #f1f3f4;
}

.v-card--outlined {
  border: 2px solid #e3f2fd !important;
}

/* Chip Enhancements */
.v-chip {
  font-weight: 500;
  border-radius: 8px !important;
}

.v-chip--pill {
  border-radius: 50px !important;
}

/* Button Enhancements */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.v-btn--rounded {
  border-radius: 8px !important;
}

/* Input Field Enhancements */
.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.v-text-field--outlined.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #1976d2 !important;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.v-select--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

/* Hide number input spin buttons */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
  text-align: center;
}

/* Pagination Enhancements */
.v-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  margin: 0 2px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.v-pagination >>> .v-pagination__item:hover {
  background-color: #e3f2fd !important;
  transform: translateY(-1px);
}

.v-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.4);
}

/* Loading Animation */
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.v-progress-circular {
  animation: pulse 2s infinite;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Spacing Utilities */
.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .enhanced-table >>> tbody td {
    padding: 12px 8px !important;
  }

  .v-card-title {
    padding: 16px !important;
  }

  .v-card-text {
    padding: 16px !important;
  }
}
</style>